import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseButton from "@reusables/BaseButton";
import BaseDatepicker from "@reusables/BaseDatepicker";
import { DatepickerRangeContext } from "@reusables/BaseDatepicker/context";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseIntegrationInject from "@reusables/BaseIntegrationInject";
import {
    useGetUnlinkedInvoicesQuery,
    useLinkInvoiceMutation
} from "@redux/features/invoiceApproval/invoiceApprovalApi";
import dayjs from "dayjs";
import { z } from "zod";
import { InvoiceApproval } from "@/types/general";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DatepickerRange } from "@reusables/BaseDatepicker/types";
import { toastError } from "@helpers/utils";
import { toast } from "react-toastify";

interface AddInvoiceModalProperties {
    isOpen: boolean;
    onClose: () => void;
    purchaseOrderId: number;
}

const schema = z.object({
    range: z.custom<DatepickerRange>(),
    invoice: z.custom<InvoiceApproval.UnlinkedInvoice>()
}).superRefine((data, ctx) => {
    if (!(data.range.from && data.range.to)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: {
                i18n: {
                    key: "general.validation.global.required"
                }
            },
            path: ["range"]
        });
    }

    if (!data.invoice) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: {
                i18n: {
                    key: "general.validation.global.required"
                }
            },
            path: ["invoice"]
        });
    }
});

type FormTyping = z.infer<typeof schema>;

export default function AddInvoiceModal(props: AddInvoiceModalProperties) {
    const { t } = useTranslation("", { keyPrefix: "purchaseOrder.general.modals.addInvoice" });

    const form = useForm<FormTyping>({
        resolver: zodResolver(schema),
        defaultValues: {
            range: {
                from: dayjs().subtract(1, "month"),
                to: dayjs()
            }
        }
    });

    const datesRange = useWatch({
        control: form.control,
        name: "range"
    });

    useEffect(() => {
        form.resetField("invoice");
    }, [datesRange]);

    const transformedDatesRange = (datesRange.from && datesRange.to) ? {
        from: datesRange.from.format("YYYY-MM-DD"),
        to: datesRange.to.format("YYYY-MM-DD")
    } : null;

    const { data: invoices, isLoading: areInvoicesLoading } = useGetUnlinkedInvoicesQuery(
        transformedDatesRange as NonNullable<typeof transformedDatesRange>,
        {
            skip: !transformedDatesRange
        }
    );

    const [linkInvoice, {isLoading: isLinkInvoiceLoading}] = useLinkInvoiceMutation();

    const onSubmit = form.handleSubmit(data => {
        linkInvoice({
            purchase_order_id: props.purchaseOrderId,
            system_id: data.invoice.system_id,
            system: "tripletex"
        })
            .unwrap()
            .then(() => {
                toast.success(t("responses.success"));
                props.onClose();
                form.resetField("invoice");
            })
            .catch(toastError);
    }, console.error);

    return (
        <BaseModal
            isOpen={props.isOpen}
            onClose={props.onClose}
            width={562}
            padding="56px"
        >
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                <div className="text-xl font-semibold text-center text-accent">{t("heading")}</div>
                <div>
                    <Controller
                        control={form.control}
                        name="range"
                        render={({ field, fieldState }) => (
                            <DatepickerRangeContext.Provider value={{
                                "range": {
                                    range: {
                                        from: field.value.from,
                                        to: field.value.to
                                    },
                                    setRange: field.onChange
                                }
                            }}>
                                <BaseDatepicker
                                    {...fieldState}

                                    label={t("date.label")}
                                    placeholder={t("date.placeholder")}
                                    rangeConfig={{ groupKey: "range", role: "solo" }}
                                />
                            </DatepickerRangeContext.Provider>
                        )}
                    />
                </div>
                <div>
                    <Controller
                        control={form.control}
                        name="invoice"
                        render={({ field, fieldState }) => (
                            <BaseDropdown
                                {...field}
                                {...fieldState}

                                label={t("externalInvoice.label")}
                                placeholder={t("externalInvoice.placeholder")}
                                options={invoices}
                                getter={{
                                    label: (opt) => opt.number + "",
                                    key: (opt) => opt.system_id,
                                    renderOption: (opt, icon) => (
                                        <div className="flex justify-between">
                                            <div className="flex space-between space-x-2">
                                                {/*TODO: replace tripletex with an actual integration slug, when Invoice Approval will support more integrations*/}
                                                <BaseIntegrationInject integration={"tripletex"}>
                                                    {
                                                        (integration) =>
                                                            <img
                                                                src={integration?.imageUrl}
                                                                alt={integration?.name}
                                                                className="w-6 h-6 p-[2px] bg-white rounded-sm object-contain"
                                                            />
                                                    }
                                                </BaseIntegrationInject>
                                                <div>{opt.number}</div>
                                            </div>
                                            {icon}
                                        </div>
                                    )
                                }}
                                isLoading={areInvoicesLoading}
                                autocomplete

                                disabled={!transformedDatesRange}
                                disableReason={t("externalInvoice.disabilityReason")}
                            />
                        )}
                    />
                </div>
                <div>
                    <BaseButton
                        className="w-full"
                        text={t("buttons.add")}
                        size="md"
                        type="submit"
                        loading={isLinkInvoiceLoading}
                    />
                </div>
            </form>
        </BaseModal>
    );
}
